import { useEffect, useState } from "react";
import { Building2, Check, ChevronDown, Database, RefreshCw } from "lucide-react";
import { useAuth } from "@/lib/auth-provider";
import { useApi } from "@/hooks/use-api";
import {
  listOrganizations,
  setActiveOrganization,
  type OrganizationRecord,
} from "@/lib/organization-client";
import {
  getStoredActiveTenantId,
  getStoredActiveTenantOrganizationId,
  setStoredActiveTenantContext,
} from "@/lib/api-client";

interface WorkspaceTenant {
  id: string;
  slug: string;
  name: string;
  status: string;
  plan: string;
  permissionLevel: string;
  accessSource: string;
}

interface WorkspaceContext {
  activeOrganization: {
    id: string;
    memberId: string;
    role: string;
  };
  activeTenant: WorkspaceTenant | null;
  tenants: WorkspaceTenant[];
}

function findStoredTenant(workspace: WorkspaceContext | null): WorkspaceTenant | null {
  const storedTenantId = getStoredActiveTenantId();
  if (!storedTenantId) return null;
  return workspace?.tenants.find((tenant) => tenant.id === storedTenantId) ?? null;
}

function shouldRepairStoredTenant(
  storedTenant: WorkspaceTenant | null,
  serverSelectedTenant: WorkspaceTenant | null,
  storedTenantId: string | null,
  storedTenantOrganizationId: string | null,
  activeOrganizationId: string | null,
): boolean {
  if (!storedTenantId) return false;
  if (!storedTenant || !serverSelectedTenant) return true;
  if (storedTenant.id === serverSelectedTenant.id) return false;

  // Legacy localStorage used to persist only a tenant id. Scope tenant storage to
  // the active org so switching workspaces cannot keep sending a stale tenant
  // header before workspace-context has a chance to repair it.
  if (!storedTenantOrganizationId || storedTenantOrganizationId !== activeOrganizationId) {
    return true;
  }

  return storedTenant.accessSource === "cross_org_assignment"
    && serverSelectedTenant.accessSource !== "cross_org_assignment";
}

function resolveDisplayedTenant(workspace: WorkspaceContext | null): WorkspaceTenant | null {
  const storedTenant = findStoredTenant(workspace);
  const storedTenantId = getStoredActiveTenantId();
  const storedTenantOrganizationId = getStoredActiveTenantOrganizationId();
  const serverSelectedTenant = workspace?.activeTenant ?? null;
  const activeOrganizationId = workspace?.activeOrganization.id ?? null;
  return shouldRepairStoredTenant(
    storedTenant,
    serverSelectedTenant,
    storedTenantId,
    storedTenantOrganizationId,
    activeOrganizationId,
  )
    ? serverSelectedTenant
    : storedTenant ?? serverSelectedTenant;
}

function initials(value: string | null | undefined): string {
  const normalized = value?.trim();
  if (!normalized) return "S";
  return normalized
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "S";
}

function tenantMeta(tenant: WorkspaceTenant | null): string {
  if (!tenant) return "No tenant selected";
  return `${tenant.slug} · ${tenant.plan} · ${tenant.permissionLevel}`;
}

function reloadWorkspace(): void {
  if (typeof window !== "undefined") {
    window.location.reload();
  }
}

export function TenantSwitcher() {
  const { session, user, refresh } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationRecord[]>([]);
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const {
    data: workspace,
    isLoading: isLoadingWorkspace,
    error: workspaceError,
  } = useApi<WorkspaceContext>("/v1/platform/workspace-context", {
    skipActiveTenantHeader: true,
  });

  useEffect(() => {
    if (!workspace) return;

    const storedTenantId = getStoredActiveTenantId();
    const storedTenantOrganizationId = getStoredActiveTenantOrganizationId();
    const activeOrganizationId = workspace.activeOrganization.id;
    const storedTenant = storedTenantId
      ? workspace.tenants.find((tenant) => tenant.id === storedTenantId) ?? null
      : null;
    const storedTenantIsAccessible = storedTenant !== null;
    const storedTenantNeedsRepair = shouldRepairStoredTenant(
      storedTenant,
      workspace.activeTenant,
      storedTenantId,
      storedTenantOrganizationId,
      activeOrganizationId,
    );
    const shouldKeepStoredTenant = storedTenantIsAccessible && !storedTenantNeedsRepair;
    const nextTenantId = shouldKeepStoredTenant
      ? storedTenantId
      : workspace.activeTenant?.id ?? null;
    const shouldUpdateStoredContext = nextTenantId !== storedTenantId
      || (nextTenantId !== null && storedTenantOrganizationId !== activeOrganizationId);

    if (shouldUpdateStoredContext) {
      setStoredActiveTenantContext(nextTenantId, nextTenantId ? activeOrganizationId : null);
      if (storedTenantId && (!storedTenantIsAccessible || storedTenantNeedsRepair)) {
        reloadWorkspace();
      }
    }
  }, [workspace]);

  useEffect(() => {
    if (!isOpen) return;

    let isCancelled = false;
    setIsLoadingOrganizations(true);
    setSwitchError(null);

    listOrganizations()
      .then((items) => {
        if (!isCancelled) setOrganizations(items);
      })
      .catch((error: unknown) => {
        if (!isCancelled) {
          setSwitchError(error instanceof Error ? error.message : "Could not load organizations.");
        }
      })
      .finally(() => {
        if (!isCancelled) setIsLoadingOrganizations(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [isOpen]);

  const activeOrganization = organizations.find((org) => org.id === session?.activeOrganizationId) ?? null;
  const activeTenant = resolveDisplayedTenant(workspace);
  const title = activeTenant?.name ?? activeOrganization?.name ?? user?.name ?? "Stero workspace";

  async function handleOrganizationSelect(organizationId: string): Promise<void> {
    if (organizationId === session?.activeOrganizationId) return;
    setSwitchError(null);
    try {
      await setActiveOrganization(organizationId);
      setStoredActiveTenantContext(null, null);
      await refresh();
      reloadWorkspace();
    } catch (error) {
      setSwitchError(error instanceof Error ? error.message : "Could not switch organization.");
    }
  }

  function handleTenantSelect(tenantId: string): void {
    if (tenantId === activeTenant?.id) return;
    setStoredActiveTenantContext(tenantId, workspace?.activeOrganization.id ?? session?.activeOrganizationId ?? null);
    reloadWorkspace();
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="flex w-full items-center gap-3 rounded-xl border border-transparent px-2 py-2 text-left transition hover:border-[var(--color-border)] hover:bg-[var(--color-surface)]"
      >
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--color-primary)] text-sm font-bold text-[var(--color-primary-foreground)]">
          {initials(title)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-[var(--color-foreground)]">
            {title}
          </p>
          <p className="truncate text-xs text-[var(--color-muted-foreground)]">
            {isLoadingWorkspace ? "Loading workspace..." : tenantMeta(activeTenant)}
          </p>
        </div>
        <ChevronDown className={`h-4 w-4 text-[var(--color-muted-foreground)] transition ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-xl">
          <div className="border-b border-[var(--color-border)] p-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
              <Building2 className="h-3.5 w-3.5" />
              Organization
            </div>
            <div className="mt-2 space-y-1">
              {isLoadingOrganizations ? (
                <div className="flex items-center gap-2 rounded-xl px-2 py-2 text-xs text-[var(--color-muted-foreground)]">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  Loading organizations
                </div>
              ) : organizations.length > 0 ? (
                organizations.map((organization) => (
                  <button
                    key={organization.id}
                    type="button"
                    onClick={() => void handleOrganizationSelect(organization.id)}
                    className="flex w-full items-center justify-between gap-2 rounded-xl px-2 py-2 text-left text-sm hover:bg-[var(--color-surface)]"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-[var(--color-foreground)]">{organization.name}</span>
                      {organization.slug ? (
                        <span className="block truncate text-xs text-[var(--color-muted-foreground)]">{organization.slug}</span>
                      ) : null}
                    </span>
                    {organization.id === session?.activeOrganizationId ? <Check className="h-4 w-4 text-[var(--color-primary)]" /> : null}
                  </button>
                ))
              ) : (
                <p className="rounded-xl px-2 py-2 text-xs text-[var(--color-muted-foreground)]">
                  No organizations are available for this session.
                </p>
              )}
            </div>
          </div>

          <div className="p-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
              <Database className="h-3.5 w-3.5" />
              Tenant context
            </div>
            <div className="mt-2 space-y-1">
              {workspaceError ? (
                <p className="rounded-xl border border-[var(--color-destructive)]/25 bg-[var(--color-destructive)]/5 px-2 py-2 text-xs text-[var(--color-destructive)]">
                  {workspaceError}
                </p>
              ) : workspace?.tenants.length ? (
                workspace.tenants.map((tenant) => (
                  <button
                    key={tenant.id}
                    type="button"
                    onClick={() => handleTenantSelect(tenant.id)}
                    className="flex w-full items-center justify-between gap-2 rounded-xl px-2 py-2 text-left text-sm hover:bg-[var(--color-surface)]"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-[var(--color-foreground)]">{tenant.name}</span>
                      <span className="block truncate text-xs text-[var(--color-muted-foreground)]">
                        {tenant.slug} · {tenant.status} · {tenant.accessSource}
                      </span>
                    </span>
                    {tenant.id === activeTenant?.id ? <Check className="h-4 w-4 text-[var(--color-primary)]" /> : null}
                  </button>
                ))
              ) : (
                <p className="rounded-xl px-2 py-2 text-xs text-[var(--color-muted-foreground)]">
                  No tenants are assigned to this organization yet.
                </p>
              )}
            </div>
          </div>

          {switchError ? (
            <div className="border-t border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-destructive)]">
              {switchError}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
